// The Lambda backend's timestamp parser rejects millisecond precision
// (`...22.233Z`). Always serialize to second precision: `YYYY-MM-DDTHH:mm:ssZ`.
export function toIsoSec(d: Date): string {
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export function nowIsoSec(): string {
  return toIsoSec(new Date());
}

// Normalize an ISO timestamp string of unknown precision (e.g. one read back
// from Firestore that Flutter wrote with milliseconds) to the backend's
// expected `YYYY-MM-DDTHH:mm:ssZ` format. Returns '' for empty/falsy input.
export function isoSecFromAny(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
  if (!m) return '';
  return `${m[1]}Z`;
}

export function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

const MONTHS_FR = [
  'janv.', 'févr.', 'mars', 'avril', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];

const WEEKDAYS_FR = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

// Format a date for human display. Accepts ISO ("2026-05-13T16:00:00Z" or
// "2026-05-13") or a Unix-seconds string ("1778263200" — what /getShifts returns).
// → "mer. 13 mai 2026". Falls back to the raw input when it doesn't parse.
export function formatHumanDate(input: string | number | null | undefined): string {
  if (input === null || input === undefined || input === '') return '';
  const s = String(input);
  const d = /^\d{9,11}$/.test(s) ? new Date(Number(s) * 1000) : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const wd = WEEKDAYS_FR[d.getDay()];
  const m = MONTHS_FR[d.getMonth()];
  return `${wd} ${d.getDate()} ${m} ${d.getFullYear()}`;
}

export function combineDateAndTime(dateYmd: string, timeHm: string): Date {
  const [y, m, d] = dateYmd.split('-').map(Number);
  const [hh, mm] = timeHm.split(':').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0, 0);
}

export function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function formatHm(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// Mirrors Flutter ShiftFormController: minutes>30 → next full half-hour, else current.
export function roundToDisplayedHalfHour(d: Date): Date {
  const c = new Date(d);
  if (c.getMinutes() > 30) {
    c.setMinutes(0, 0, 0);
    c.setHours(c.getHours() + 1);
  } else {
    c.setMinutes(0, 0, 0);
  }
  return c;
}
